import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTokenForRequest } from '../../utils/apiAuth';
import './StudentProgressReport.css';
import Select from 'react-select';
import StudentReviewModal from '../StudentReviewModal/StudentReviewModal';

// Global axios interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      window.location.href = '/login.aspx';
      return;
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function to encode token for API calls
 * @param {string} token - The session token
 * @returns {string} - Encoded token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * StudentProgressReport Component
 * Displays student progress reports with filtering capabilities
 */
const StudentProgressReport = () => {
  // State management
  const [semesters, setSemesters] = useState([]);
  const [modules, setModules] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [reportMode, setReportMode] = useState('Topic');
  const [progress, setProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAllSemesters, setShowAllSemesters] = useState(false);
  const [studentProgress, setStudentProgress] = useState([]);
  const [studentProgressUnplanned, setStudentProgressUnplanned] = useState([]);
  const [allClassStudents, setAllClassStudents] = useState([]);
  const [allTopicData, setAllTopicData] = useState({});
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    topicId: null,
    studentId: null,
    msID: null,
    topicName: null,
    studentName: null
  });
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [isStudentReportCollapsed, setIsStudentReportCollapsed] = useState(true);
  const [collapsedTopicClasses, setCollapsedTopicClasses] = useState(new Set());
  const [collapsedLeftClasses, setCollapsedLeftClasses] = useState(new Set());

  // Fetch all module semesters on component mount
  useEffect(() => {
    fetchAllModuleSemesters();
  }, []);

  // This useEffect is now mainly for handling edge cases where auto-load didn't happen
  // The main auto-loading is handled in fetchAllModuleSemesters
  useEffect(() => {
    // Only auto-load if we haven't done it yet and we have data with selections
    if (semesters.length > 0 && modules.length > 0 && !hasAutoLoaded) {
      const selectedSemesterIds = semesters.filter(s => s.selected).map(s => s.ID);
      const selectedModules = modules.filter(m => m.selected && selectedSemesterIds.includes(m.SemesterID || m.Semester));
      
      // If we have selected modules but haven't loaded yet, trigger the report
      if (selectedModules.length > 0) {
        setHasAutoLoaded(true);
        setTimeout(() => {
          handleGenerateReport();
        }, 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semesters, modules, hasAutoLoaded]);

  // Auto-select and fetch first topic's progress after topics are loaded
  useEffect(() => {
    if (topics.length > 0) {
      const firstModuleGroup = topics[0];
      if (firstModuleGroup && firstModuleGroup.topics && firstModuleGroup.topics.length > 0) {
        handleTopicClick(firstModuleGroup.topics[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics]);

  // By Student: when class list is available, start with all class panels collapsed
  useEffect(() => {
    const source = allClassStudents.length > 0 ? allClassStudents : progress;
    if (source.length > 0) {
      const allClassIds = source.map(c => c.ClassID);
      setCollapsedLeftClasses(new Set(allClassIds));
    }
  }, [allClassStudents, progress]);

  // API calls
  const fetchAllModuleSemesters = async () => {
    try {
      setIsLoading(true);
      const token = getTokenForRequest();
      const response = await axios.get('/webservices/Report.svc/moduleSemesters', {
        params: {
          token: encodeToken(token)
        }
      });
      
      const { AllSemesters, ModuleSemesters } = response.data;
      
      // Ensure we have arrays
      const semestersArray = Array.isArray(AllSemesters) ? AllSemesters : [];
      const modulesArray = Array.isArray(ModuleSemesters) ? ModuleSemesters : [];
      
      // Auto-select first semester and module if none are selected
      if (semestersArray.length > 0 && modulesArray.length > 0) {
        const firstSemesterId = semestersArray[0].ID;
        const updatedSemesters = semestersArray.map(s => ({ ...s, selected: s.ID === firstSemesterId }));
        const modulesInSemester = modulesArray.filter(m => m.SemesterID === firstSemesterId || m.Semester === firstSemesterId);
        
        if (modulesInSemester.length > 0) {
          const firstModuleId = modulesInSemester[0].ID;
          const updatedModules = modulesArray.map(m => ({ ...m, selected: m.ID === firstModuleId }));
          setSemesters(updatedSemesters);
          setModules(updatedModules);
          
          // Auto-load report after setting selections
          // Use a small delay to ensure state is updated, then call handleGenerateReport
          // This simulates clicking the "Get Report" button automatically
          setTimeout(() => {
            setHasAutoLoaded(true);
            handleGenerateReport(updatedModules).catch(err => {
              console.error('Error in auto-loading report:', err);
              setIsLoading(false);
            });
          }, 200);
        } else {
          setSemesters(updatedSemesters);
          setModules(modulesArray);
          setIsLoading(false);
        }
      } else {
        setSemesters(semestersArray);
        setModules(modulesArray);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async (modulesToUse = null) => {
    try {
      setIsLoading(true);
      const token = getTokenForRequest();
      // Get selected modules - use provided modules or fall back to state
      // Ensure modulesList is always an array
      const modulesList = Array.isArray(modulesToUse) ? modulesToUse : (Array.isArray(modules) ? modules : []);
      
      if (!Array.isArray(modulesList) || modulesList.length === 0) {
        setIsLoading(false);
        return;
      }
      
      const selectedModules = modulesList.filter(m => m && m.selected).map(m => m.ID);
      
      if (selectedModules.length === 0) {
        setIsLoading(false);
        return;
      }
      
      // Call API for each selected module
      const allTopics = [];
      let allClassStudentsTemp = [];
      const topicDataMap = {};
      for (const moduleId of selectedModules) {
        const response = await axios.post('/webservices/Organization.svc/TopicsInModuleSemesters', {
          token: encodeToken(getTokenForRequest()),
          ms: moduleId.toString()
        });
        if (response.data) {
          // Add module info to each topic
          const moduleInfo = modulesList.find(m => m && m.ID === moduleId);
          // Ensure response.data is an array before mapping
          const topicsData = Array.isArray(response.data) ? response.data : [];
          const topicsWithModule = topicsData.map(topic => ({
            ...topic,
            SemesterName: moduleInfo?.Semester || '',
            ModuleName: moduleInfo?.Module || ''
          }));
          allTopics.push(...topicsWithModule);
          // If response includes class-student structure, set it
          if (response.data.ClassStudents) {
            allClassStudentsTemp = allClassStudentsTemp.concat(response.data.ClassStudents);
          }
          // For each topic, fetch and store its progress and details
          for (const topic of topicsWithModule) {
            const topicProgressResp = await axios.post('/webservices/Organization.svc/SPByTopic', {
              token: encodeToken(getTokenForRequest()),
              topicId: topic.TopicID,
              ms: moduleId.toString()
            });
            if (topicProgressResp.data) {
              topicDataMap[topic.TopicID] = {
                items: topicProgressResp.data.Items || [],
                classStudents: topicProgressResp.data.ClassStudents || [],
              };
            }
          }
        }
      }
      // Group topics by module
      const groupedTopics = allTopics.reduce((acc, topic) => {
        const key = `${topic.SemesterName} - ${topic.ModuleName}`;
        if (!acc[key]) {
          acc[key] = {
            SemesterName: topic.SemesterName,
            ModuleName: topic.ModuleName,
            topics: []
          };
        }
        acc[key].topics.push(topic);
        return acc;
      }, {});
      setTopics(Object.values(groupedTopics));
      // Set allClassStudents if found, else clear
      setAllClassStudents(allClassStudentsTemp.length > 0 ? allClassStudentsTemp : []);
      // Store all topic data
      setAllTopicData(topicDataMap);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTopicClick = async (topic) => {
    try {
      setIsLoading(true);
      const token = getTokenForRequest();
      // Get all selected module semester IDs
      const selectedModuleIds = modules.filter(m => m.selected).map(m => m.ID.toString());
      const response = await axios.post('/webservices/Organization.svc/SPByTopic', {
          token: encodeToken(getTokenForRequest()),
        topicId: topic.TopicID,
        ms: selectedModuleIds.join(',')
      });
      if (response.data) {
        setSelectedTopic(topic);
        const classStudents = response.data.ClassStudents || [];
        setProgress(classStudents);
        setItems(response.data.Items || []);
        // Initialize all classes as collapsed (add all ClassIDs to the Set)
        const allClassIds = new Set(classStudents.map(c => c.ClassID).filter(id => id != null));
        setCollapsedTopicClasses(allClassIds);
      } else {
        setSelectedTopic(topic);
        setProgress([]);
        setItems([]);
        setCollapsedTopicClasses(new Set());
      }
    } catch (error) {
      console.error('Error loading topic progress:', error);
      setSelectedTopic(topic);
      setProgress([]);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = () => {
    // Get selected module semester IDs
    const selectedModuleIds = modules.filter(m => m.selected).map(m => m.ID);
    
    if (selectedModuleIds.length === 0) {
      alert('Please select at least one module to export!');
      return;
    }

    // Use the first selected module ID (or join if multiple are selected)
    // Based on the example, ms appears to be a single value, but we'll support comma-separated if needed
    const msValue = selectedModuleIds.join(',');
    
    // Build the download URL
    const downloadUrl = `DownloadReport.aspx?ReportType=PROGRESSREPORTOVERALL&ms=${msValue}`;
    
    // Use downloadURL if available globally, otherwise create a temporary link
    if (typeof window.downloadURL === 'function') {
      window.downloadURL(downloadUrl);
    } else {
      // Fallback: create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper: Get selected semester IDs
  const selectedSemesterIds = semesters.filter(s => s.selected).map(s => s.ID);

  // Filter modules to only those in selected semesters
  const filteredModules = modules.filter(m => selectedSemesterIds.includes(m.SemesterID || m.Semester));

  // Prepare options for react-select
  const semesterOptions = semesters.map(s => ({
    value: s.ID,
    label: s.Name,
    selected: s.selected
  }));
  const moduleOptions = modules
    .filter(m => selectedSemesterIds.includes(m.SemesterID || m.Semester))
    .map(m => ({
      value: m.ID,
      label: `${m.Semester} - ${m.Module}`,
      selected: m.selected
    }));

  // When a semester is unchecked, deselect modules in that semester
  const handleSemesterChange = (semester, checked) => {
    setSemesters(semesters.map(s =>
      s.ID === semester.ID ? { ...s, selected: checked } : s
    ));
    // Deselect modules if their semester is now unchecked
    if (!checked) {
      setModules(modules.map(m =>
        (m.SemesterID === semester.ID || m.Semester === semester.ID) ? { ...m, selected: false } : m
      ));
    }
  };

  // Handler for selecting a student
  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setIsStudentReportCollapsed(false); // Open (expand) the student report when a student is clicked
    setIsLoading(true);
    try {
      const token = getTokenForRequest();
      // Call the API for student progress - get all data (no ms parameter like old code)
      const response = await axios.post('/webservices/Organization.svc/SPByStudent', {
        token: encodeToken(getTokenForRequest()),
        studentId: student.ID
      });
      
      // Get selected module semester IDs for filtering
      const selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
      
      // Filter data client-side like old code
      let allData = [];
      if (Array.isArray(response.data)) {
        allData = response.data;
      } else if (response.data && response.data.Items) {
        allData = response.data.Items;
      }
      
      // Filter topics based on selected modules (like old code - doesn't filter by InCurrentSemester)
      // The old code only uses visibleByEducator which is based on selected modules, not InCurrentSemester
      const filteredItems = allData.filter(topic => {
        if (!topic.Details || topic.Details.length === 0) return false;
        
        // Check if any detail matches selected modules (like old code)
        // Old code sets visibleByEducator = true when ModuleSemesterID matches selected modules
        return topic.Details.some(detail => {
          return selectedModuleIds.includes(Number(detail.ModuleSemesterID));
        });
      });
      
      setItems(filteredItems);
    } catch (error) {
      console.error('Error loading student progress:', error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get the most relevant detail (current semester if possible)
  // Deduplicates by SemesterID to avoid double counting when same topic appears in multiple module semesters
  // Returns the detail with the correct ModuleSemesterID for the review API
  function getRelevantDetail(details, selectedModuleIds = null) {
    if (!details || details.length === 0) return null;
    
    // Get selected module IDs if not provided
    if (!selectedModuleIds) {
      selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
    }
    
    // Deduplicate by SemesterID - keep the one with highest level (like Angular code does)
    // This prevents double counting when the same topic appears in multiple module semesters
    // Note: We deduplicate by SemesterID, but preserve ModuleSemesterID for the review API
    const uniqueDetailsBySemester = {};
    
    details.forEach(detail => {
      // Use SemesterID for deduplication (same as Angular code)
      // If SemesterID is not available, fall back to ModuleSemesterID
      const semesterId = detail.SemesterID || detail.ModuleSemesterID;
      const existingDetail = uniqueDetailsBySemester[semesterId];
      
      if (!existingDetail) {
        // First time seeing this SemesterID
        uniqueDetailsBySemester[semesterId] = detail;
      } else {
        // If we've seen this SemesterID, prefer the one that matches selected modules
        // If both match or neither matches, keep the one with higher level
        const detailMatchesSelected = selectedModuleIds.length > 0 && 
          selectedModuleIds.includes(Number(detail.ModuleSemesterID));
        const existingMatchesSelected = selectedModuleIds.length > 0 && 
          selectedModuleIds.includes(Number(existingDetail.ModuleSemesterID));
        
        if (detailMatchesSelected && !existingMatchesSelected) {
          // New detail matches selected modules, existing doesn't - prefer new
          uniqueDetailsBySemester[semesterId] = detail;
        } else if (!detailMatchesSelected && existingMatchesSelected) {
          // Existing matches selected modules, new doesn't - keep existing
          // Do nothing
        } else if ((detail.Level || 0) > (existingDetail.Level || 0)) {
          // Both match or neither matches - keep the one with higher level
          uniqueDetailsBySemester[semesterId] = detail;
        }
      }
    });
    
    const uniqueDetails = Object.values(uniqueDetailsBySemester);
    
    // Prefer details that match selected modules and are in current semester
    // Then prefer InCurrentSemester
    // Then prefer highest level
    // Finally fall back to last
    if (selectedModuleIds.length > 0) {
      const matchingSelected = uniqueDetails.filter(d => 
        selectedModuleIds.includes(Number(d.ModuleSemesterID))
      );
      if (matchingSelected.length > 0) {
        const currentSemesterMatching = matchingSelected.find(d => d.InCurrentSemester);
        if (currentSemesterMatching) return currentSemesterMatching;
        
        const highestLevelMatching = matchingSelected.reduce((max, d) => 
          (d.Level || 0) > (max.Level || 0) ? d : max, matchingSelected[0]
        );
        return highestLevelMatching;
      }
    }
    
    // Fallback: prefer InCurrentSemester, else highest level, else last
    const currentSemesterDetail = uniqueDetails.find(d => d.InCurrentSemester);
    if (currentSemesterDetail) return currentSemesterDetail;
    
    const highestLevelDetail = uniqueDetails.reduce((max, d) => 
      (d.Level || 0) > (max.Level || 0) ? d : max, uniqueDetails[0]
    );
    return highestLevelDetail || uniqueDetails[uniqueDetails.length - 1];
  }

  /**
   * Handles clicking on a progress bar to review questions
   */
  const handleProgressBarClick = (topic, progress, student, msID) => {
    if (!progress || progress.NumOfQuestionTried <= 0) {
      return; // Don't open if no questions attempted
    }

    const topicId = topic?.TopicID || topic;
    const studentId = student?.ID || student;
    const topicName = topic?.TopicName || selectedTopic?.TopicName || 'Topic';
    const studentName = student?.DisplayName || student?.StudentName || 'Student';

    setReviewModal({
      isOpen: true,
      topicId: topicId,
      studentId: studentId,
      msID: msID || progress?.ModuleSemesterID || 0,
      topicName: topicName,
      studentName: studentName
    });
  };

  /**
   * Closes the review modal
   */
  const handleCloseReviewModal = () => {
    setReviewModal({
      isOpen: false,
      topicId: null,
      studentId: null,
      msID: null,
      topicName: null,
      studentName: null
    });
  };

  // Place this levels array at the top-level scope if not already available
  const levels = [
    { name: 'In Progress', className: 'in-progress', color: '#d9534f' },
    { name: 'Beginner', className: 'beginner', color: '#f0ad4e' },
    { name: 'Adv. Beginner', className: 'adv-beginner', color: '#ffe135' },
    { name: 'Competent', className: 'competent', color: '#5bc0de' },
    { name: 'Expert', className: 'expert', color: '#5cb85c' },
  ];

  // Add custom styles for react-select to wrap selected values
  const selectStyles = {
    valueContainer: (base) => ({
      ...base,
      flexWrap: 'wrap',
      maxHeight: 80,
      overflowY: 'auto',
    }),
    multiValue: (base) => ({
      ...base,
      margin: '2px 2px',
    }),
  };

  return (
    <div className="student-progress">
      {/* Show loading indicator if initial data is loading */}
      {isLoading && semesters.length === 0 && (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading student progress data...</p>
        </div>
      )}
      {/* Filters Section */}
      <div className="filters-section mb-4">
        <div className="ic-filters">
          <div style={{ minWidth: 250, marginBottom: 12 }}>
            <label style={{ fontWeight: 500 }}>Semesters</label>
            <Select
              isMulti
              options={semesterOptions}
              value={semesterOptions.filter(o => o.selected)}
              onChange={selected => {
                const selectedIds = selected ? selected.map(o => o.value) : [];
                setSemesters(semesters.map(s => ({
                  ...s,
                  selected: selectedIds.includes(s.ID)
                })));
              }}
              placeholder="Select Semesters..."
              closeMenuOnSelect={false}
              isClearable={true}
              styles={selectStyles}
            />
          </div>
          <div style={{ minWidth: 300, marginBottom: 12 }}>
            <label style={{ fontWeight: 500 }}>Modules</label>
            <Select
              isMulti
              options={moduleOptions}
              value={moduleOptions.filter(o => o.selected)}
              onChange={selected => {
                const selectedIds = selected ? selected.map(o => o.value) : [];
                setModules(modules.map(m => ({
                  ...m,
                  selected: selectedIds.includes(m.ID)
                })));
              }}
              placeholder="Select Modules..."
              closeMenuOnSelect={false}
              isClearable={true}
              styles={selectStyles}
            />
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleGenerateReport}
          >
            Get Report
          </button>
          <button 
            className="btn btn-warning"
            onClick={handleExportReport}
            title="Export report"
          >
            Export Report
          </button>
        </div>
      </div>

      {/* Report Content */}
      {topics.length === 0 ? (
        <div className="report-content">
          <div className="row">
            <div className="col-sm-12">
              <h4>No data available</h4>
            </div>
          </div>
        </div>
      ) : (
        <div className="report-content">
          <div className="row">
            {/* Left Sidebar */}
            <div className="col-md-3">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item">
                <button 
                  className={`nav-link ${reportMode === 'Topic' ? 'active' : ''}`}
                  onClick={() => setReportMode('Topic')}
                >
                  By Topic
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${reportMode === 'Student' ? 'active' : ''}`}
                  onClick={() => setReportMode('Student')}
                >
                  By Student
                </button>
              </li>
            </ul>

            <div className="tab-content mt-3">
              {reportMode === 'Topic' ? (
                <div className="topics-list">
                  {topics.map(moduleGroup => (
                    <div key={`${moduleGroup.SemesterName}-${moduleGroup.ModuleName}`}>
                      <div className="module-title">
                        {moduleGroup.SemesterName} - {moduleGroup.ModuleName}
                      </div>
                      {moduleGroup.topics.map(topic => (
                    <div 
                      key={topic.TopicID}
                      className={`topic-item ${selectedTopic?.TopicID === topic.TopicID ? 'selected' : ''}`}
                          onClick={() => handleTopicClick(topic)}
                    >
                      {topic.TopicName}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="students-list">
                  {/* Group students by class - each class is collapsible */}
                  {(allClassStudents.length > 0 ? allClassStudents : progress).map(classData => {
                    const isClassCollapsed = collapsedLeftClasses.has(classData.ClassID);
                    return (
                      <div key={classData.ClassID} className="panel panel-default">
                        <div
                          className="panel-heading"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => {
                            const next = new Set(collapsedLeftClasses);
                            if (isClassCollapsed) next.delete(classData.ClassID);
                            else next.add(classData.ClassID);
                            setCollapsedLeftClasses(next);
                          }}
                        >
                          <h4 className="panel-title" style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{classData.ClassName}</span>
                            <span style={{ fontSize: '1rem', transition: 'transform 0.3s', transform: isClassCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                          </h4>
                        </div>
                        <div
                          className="panel-body"
                          style={{
                            maxHeight: isClassCollapsed ? 0 : '10000px',
                            overflow: 'hidden',
                            transition: 'max-height 0.3s ease-out',
                            padding: isClassCollapsed ? 0 : undefined,
                            margin: 0,
                            border: 'none'
                          }}
                        >
                          <ul className="module-topics" style={{ listStyle: 'none', paddingLeft: 0 }}>
                            {classData.Students?.map(student => (
                              <li
                                key={student.ID}
                                className={`module-topic${selectedStudent?.ID === student.ID ? ' selected' : ''}`}
                                style={{ cursor: 'pointer', padding: '4px 0', fontWeight: selectedStudent?.ID === student.ID ? 'bold' : 'normal' }}
                                onClick={() => handleStudentClick(student)}
                              >
                                {student.DisplayName}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Content */}
          <div className="col-md-9" style={{ borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
            {/* By Topic */}
            <div className="report-by-topic" style={{ display: reportMode === 'Topic' ? 'block' : 'none' }}>
              {!progress || progress.length === 0 || !progress.some(classData => classData.Students && classData.Students.length > 0) ? (
                <div className="row class">
                  <div className="col-sm-12">
                    <h4>No data available</h4>
                  </div>
                </div>
              ) : (
                progress.map((classData, index) => {
                  // Skip rendering if no students in this class
                  if (!classData.Students || classData.Students.length === 0) {
                    return null;
                  }
                  const isCollapsed = collapsedTopicClasses.has(classData.ClassID);
                  return (
                    <div key={classData.ClassID} className="panel panel-default row" style={{ marginBottom: '5px' }}>
                      <div 
                        className="panel-heading"
                        style={{ 
                          cursor: 'pointer',
                          padding: '12px 15px',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => {
                          const newCollapsed = new Set(collapsedTopicClasses);
                          if (isCollapsed) {
                            newCollapsed.delete(classData.ClassID);
                          } else {
                            newCollapsed.add(classData.ClassID);
                          }
                          setCollapsedTopicClasses(newCollapsed);
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                      >
                        <h4 className="panel-title" style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>
                            <span className="class-name">{selectedTopic.TopicName}</span>
                            {' - '}
                            <span className="class-name">{classData.ClassName}</span>
                          </span>
                          <span 
                            style={{ 
                              fontSize: '1rem',
                              transition: 'transform 0.3s',
                              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              marginLeft: '10px'
                            }}
                          >
                            ▼
                          </span>
                        </h4>
                      </div>
                      <div 
                        className="topic-class-content"
                        style={{ 
                          maxHeight: isCollapsed ? 0 : '10000px',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease-out, opacity 0.3s ease-out',
                          opacity: isCollapsed ? 0 : 1,
                          paddingBottom: isCollapsed ? 0 : '40px'
                        }}
                      >
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>Student Name</th>
                                <th>Topic Progress</th>
                              </tr>
                              <tr>
                                <th></th>
                                <th style={{ padding: 0 }}>
                                  <div style={{ display: 'flex', height: 24, minWidth: 200 }}>
                                    <div style={{ flex: 1, background: '#d9534f', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 13, borderRight: '1px solid #fff' }}>In Progress</div>
                                    <div style={{ flex: 1, background: '#f0ad4e', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 13, borderRight: '1px solid #fff' }}>Beginner</div>
                                    <div style={{ flex: 1, background: '#ffe135', color: '#000', textAlign: 'center', lineHeight: '24px', fontSize: 13, borderRight: '1px solid #fff' }}>Adv</div>
                                    <div style={{ flex: 1, background: '#5bc0de', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 13, borderRight: '1px solid #fff' }}>Competent</div>
                                    <div style={{ flex: 1, background: '#5cb85c', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 13 }}>Expert</div>
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {classData.Students?.map(student => {
                                // Find progress for this student in Items
                                const item = items.find(i => i.StudentID === student.ID);
                                // Get selected module IDs to ensure we use the correct ModuleSemesterID
                                const selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
                                // Use getRelevantDetail to avoid double counting when same topic appears in multiple module semesters
                                const detail = item && item.Details ? getRelevantDetail(item.Details, selectedModuleIds) : null;
                                const level = detail ? detail.Level : null;
                                const numQuestions = detail ? detail.NumOfQuestionTried : 0;
                                return (
                                  <tr key={student.ID}>
                                    <td>{student.DisplayName}</td>
                                    <td>
                                      <StudentProgressBar
                                        level={level}
                                        numQuestions={numQuestions}
                                        onClick={() => {
                                          const item = items.find(i => i.StudentID === student.ID);
                                          const selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
                                          const detail = item && item.Details ? getRelevantDetail(item.Details, selectedModuleIds) : null;
                                          const msID = detail ? detail.ModuleSemesterID : null;
                                          handleProgressBarClick(selectedTopic, detail, student, msID);
                                        }}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* By Student */}
            <div className="report-by-student" style={{ display: reportMode === 'Student' ? 'block' : 'none' }}>
              {selectedStudent ? (
                (!items || items.length === 0) ? (
                  <h4>No data available</h4>
                ) : (
                  <div className="panel panel-default row" style={{ marginBottom: '5px' }}>
                    <div
                      className="panel-heading"
                      style={{
                        cursor: 'pointer',
                        padding: '12px 15px',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => setIsStudentReportCollapsed(!isStudentReportCollapsed)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                    >
                      <h4 className="panel-title" style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span className="class-name">{selectedStudent.DisplayName}</span>
                        <span
                          style={{
                            fontSize: '1rem',
                            transition: 'transform 0.3s',
                            transform: isStudentReportCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                            marginLeft: '10px'
                          }}
                        >
                          ▼
                        </span>
                      </h4>
                    </div>
                    <div
                      className="topic-class-content"
                      style={{
                        maxHeight: isStudentReportCollapsed ? 0 : '10000px',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease-out, opacity 0.3s ease-out',
                        opacity: isStudentReportCollapsed ? 0 : 1,
                        paddingBottom: isStudentReportCollapsed ? 0 : '40px'
                      }}
                    >
                      <div style={{ margin: '16px 0' }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={showAllSemesters}
                            onChange={e => setShowAllSemesters(e.target.checked)}
                          />
                          {' '}Show Student Achievements for All Semesters
                        </label>
                      </div>
                      {/* Planned Topics */}
                      {(() => {
                        const plannedTopics = Array.isArray(items) ? items.filter(t => t.IsPlanned === true || t.IsPlanned === 'true') : [];
                        const selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
                        return plannedTopics.length === 0 ? (
                          <h4>No data available</h4>
                        ) : (
                          plannedTopics.map(topic => {
                            // Mark details with visibleByEducator (like old code)
                            // Old code: visibleByEducator = true when ModuleSemesterID matches selected modules
                            const detailsWithVisibility = (topic.Details || []).map(detail => ({
                              ...detail,
                              visibleByEducator: selectedModuleIds.includes(Number(detail.ModuleSemesterID))
                            }));
                            
                            // Filter details to show: visibleByEducator || showAllSemesters (like old code)
                            // Old code doesn't filter by InCurrentSemester - it only uses visibleByEducator
                            let visibleDetails = detailsWithVisibility.filter(detail => 
                              detail.visibleByEducator || showAllSemesters
                            );
                            
                            // Deduplicate by ModuleSemesterID to avoid showing same detail multiple times
                            // This prevents double counting when API returns duplicate Details
                            const uniqueDetailsByMS = {};
                            visibleDetails.forEach(detail => {
                              const msId = detail.ModuleSemesterID;
                              if (!uniqueDetailsByMS[msId]) {
                                uniqueDetailsByMS[msId] = detail;
                              } else {
                                // If duplicate, keep the one with higher level
                                if ((detail.Level || 0) > (uniqueDetailsByMS[msId].Level || 0)) {
                                  uniqueDetailsByMS[msId] = detail;
                                }
                              }
                            });
                            visibleDetails = Object.values(uniqueDetailsByMS);
                            
                            // When showAllSemesters is false, deduplicate by SemesterID to avoid showing
                            // multiple progress bars for same semester (like getRelevantDetail does)
                            if (!showAllSemesters) {
                              const uniqueBySemester = {};
                              visibleDetails.forEach(detail => {
                                const semesterId = detail.SemesterID || detail.ModuleSemesterID;
                                if (!uniqueBySemester[semesterId]) {
                                  uniqueBySemester[semesterId] = detail;
                                } else {
                                  // Keep the one with higher level or that matches selected modules
                                  const existing = uniqueBySemester[semesterId];
                                  const detailMatches = selectedModuleIds.includes(Number(detail.ModuleSemesterID));
                                  const existingMatches = selectedModuleIds.includes(Number(existing.ModuleSemesterID));
                                  
                                  if (detailMatches && !existingMatches) {
                                    uniqueBySemester[semesterId] = detail;
                                  } else if (!detailMatches && existingMatches) {
                                    // Keep existing
                                  } else if ((detail.Level || 0) > (existing.Level || 0)) {
                                    uniqueBySemester[semesterId] = detail;
                                  }
                                }
                              });
                              visibleDetails = Object.values(uniqueBySemester);
                            }
                            
                            // Sort by ModuleSemesterID descending (like old code: orderBy : '-ModuleSemesterID')
                            visibleDetails.sort((a, b) => (b.ModuleSemesterID || 0) - (a.ModuleSemesterID || 0));
                            
                            if (visibleDetails.length === 0) {
                              return null;
                            }
                            
                            return (
                              <div key={topic.TopicID} style={{ marginBottom: 15 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{topic.TopicName}</div>
                                <div>
                                  {visibleDetails.map((detail, idx) => (
                                    <div key={`${detail.ModuleSemesterID}-${idx}`} style={{ marginBottom: 8 }}>
                                      <StudentProgressBar
                                        level={detail ? detail.Level : 0}
                                        numQuestions={detail ? detail.NumOfQuestionTried : 0}
                                        onClick={() => {
                                          const msID = detail ? detail.ModuleSemesterID : null;
                                          handleProgressBarClick(topic, detail, selectedStudent, msID);
                                        }}
                                        title={`${detail.SemesterName || ''} / ${detail.ModuleName || ''} / ${detail.ClassName || ''}`}
                                      />
                                    </div>
                                  ))}
                                  <div style={{ display: 'flex', height: 28, minWidth: 200, marginTop: 2 }}>
                                    {levels.map((lvl, idx) => (
                                      <div
                                        key={lvl.className}
                                        style={{
                                          flex: 1,
                                          textAlign: 'center',
                                          fontSize: 13,
                                          color: '#222',
                                          borderTop: `4px solid ${lvl.color}`,
                                          marginRight: idx < 4 ? 1 : 0,
                                          paddingTop: 2,
                                          fontWeight: 400,
                                          background: 'transparent'
                                        }}
                                      >
                                        {lvl.name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        );
                      })()}
                      {/* Unplanned Topics */}
                      {(() => {
                        const unplannedTopics = Array.isArray(items) ? items.filter(t => t.IsPlanned === false || t.IsPlanned === 'false') : [];
                        const selectedModuleIds = modules.filter(m => m.selected).map(m => Number(m.ID));
                        return unplannedTopics.length > 0 && (
                          <div>
                            <h3 style={{ fontWeight: 'bold' }}>Unplanned Topics</h3>
                            {unplannedTopics.map(topic => {
                              // Mark details with visibleByEducator (like old code)
                              // Old code: visibleByEducator = true when ModuleSemesterID matches selected modules
                              const detailsWithVisibility = (topic.Details || []).map(detail => ({
                                ...detail,
                                visibleByEducator: selectedModuleIds.includes(Number(detail.ModuleSemesterID))
                              }));
                              
                              // Filter details to show: visibleByEducator || showAllSemesters (like old code)
                              // Old code doesn't filter by InCurrentSemester - it only uses visibleByEducator
                              const visibleDetails = detailsWithVisibility.filter(detail => 
                                detail.visibleByEducator || showAllSemesters
                              );
                              
                              // Sort by ModuleSemesterID descending (like old code: orderBy : '-ModuleSemesterID')
                              visibleDetails.sort((a, b) => (b.ModuleSemesterID || 0) - (a.ModuleSemesterID || 0));
                              
                              if (visibleDetails.length === 0) {
                                return null;
                              }
                              
                              return (
                                <div key={topic.TopicID} style={{ marginBottom: 15 }}>
                                  <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{topic.TopicName}</div>
                                  <div>
                                    {visibleDetails.map((detail, idx) => (
                                      <div key={`${detail.ModuleSemesterID}-${idx}`} style={{ marginBottom: 8 }}>
                                        <StudentProgressBar
                                          level={detail ? detail.Level : 0}
                                          numQuestions={detail ? detail.NumOfQuestionTried : 0}
                                          onClick={() => {
                                            const msID = detail ? detail.ModuleSemesterID : null;
                                            handleProgressBarClick(topic, detail, selectedStudent, msID);
                                          }}
                                          title={`${detail.SemesterName || ''} / ${detail.ModuleName || ''} / ${detail.ClassName || ''}`}
                                        />
                                      </div>
                                    ))}
                                    <div style={{ display: 'flex', height: 28, minWidth: 200, marginTop: 2 }}>
                                      {levels.map((lvl, idx) => (
                                        <div
                                          key={lvl.className}
                                          style={{
                                            flex: 1,
                                            textAlign: 'center',
                                            fontSize: 13,
                                            color: '#222',
                                            borderTop: `4px solid ${lvl.color}`,
                                            marginRight: idx < 4 ? 1 : 0,
                                            paddingTop: 2,
                                            fontWeight: 400,
                                            background: 'transparent'
                                          }}
                                        >
                                          {lvl.name}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )
              ) : (
                <h4>Please select a student</h4>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Review Modal */}
      <StudentReviewModal
        isOpen={reviewModal.isOpen}
        onClose={handleCloseReviewModal}
        topicId={reviewModal.topicId}
        studentId={reviewModal.studentId}
        msID={reviewModal.msID}
        topicName={reviewModal.topicName}
        studentName={reviewModal.studentName}
      />
    </div>
  );
};

const GetLevelName = (levelNo) => {
  switch(parseInt(levelNo)) {
    case 0: return 'In Progress';
    case 1: return 'Beginner';
    case 2: return 'Adv. Beginner';
    case 3: return 'Competent';
    case 4: return 'Expert';
    default: return 'In Progress';
  }
};

/**
 * Renders a horizontal progress bar for a student's topic progress.
 * @param {number} level - The student's progress level (0-4).
 * @param {number} numQuestions - Number of questions attempted.
 * @param {function} onClick - Optional click handler for reviewing questions.
 * @param {string} title - Optional tooltip text.
 */
function StudentProgressBar({ level, numQuestions, onClick, title }) {
  const levels = [
    { name: 'In Progress', className: 'in-progress', color: '#d9534f' },
    { name: 'Beginner', className: 'beginner', color: '#f0ad4e' },
    { name: 'Adv', className: 'adv-beginner', color: '#ffe135' },
    { name: 'Competent', className: 'competent', color: '#5bc0de' },
    { name: 'Expert', className: 'expert', color: '#5cb85c' },
  ];
  const fillColor = level != null && level >= 0 ? levels[level].color : '#eee';
  const isClickable = onClick && numQuestions > 0;
  
  return (
    <div 
      style={{ 
        display: 'flex', 
        height: 24, 
        minWidth: 200,
        cursor: isClickable ? 'pointer' : 'default'
      }}
      onClick={isClickable ? onClick : undefined}
      title={title || (isClickable ? 'Click to review questions' : '')}
    >
      {levels.map((lvl, idx) => (
        <div
          key={lvl.className}
          style={{
            flex: 1,
            background: idx <= level ? fillColor : '#eee',
            color: idx === level ? '#fff' : 'transparent',
            textAlign: 'center',
            lineHeight: '24px',
            borderRight: idx < 4 ? '1px solid #fff' : 'none',
            fontWeight: idx === level ? 'bold' : 'normal',
            position: 'relative',
            fontSize: 16,
            transition: 'background 0.2s',
            opacity: isClickable ? 1 : 1
          }}
        >
          {idx === level && numQuestions > 0 ? numQuestions : 0}
        </div>
      ))}
    </div>
  );
}

export default StudentProgressReport; 